import config from "@web-app/config";
import http from "@web-app/utils/http";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    !!config.VAPID_PUBLIC_KEY
  );
}

export function getPushPermission(): NotificationPermission | null {
  if (!isPushSupported()) return null;
  return Notification.permission;
}

async function subscribePush(): Promise<void> {
  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      applicationServerKey: urlBase64ToUint8Array(config.VAPID_PUBLIC_KEY)
        .buffer as ArrayBuffer,
      userVisibleOnly: true,
    });
  }

  const subJSON = subscription.toJSON();
  await http.post("/push/subscribe", {
    endpoint: subJSON.endpoint,
    keys: {
      auth: subJSON.keys?.auth,
      p256dh: subJSON.keys?.p256dh,
    },
  });
}

export async function registerPushNotifications(): Promise<void> {
  if (!isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  await subscribePush();
}

export async function requestAndRegisterPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  await subscribePush();
  return true;
}

export async function unregisterPushNotifications(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  try {
    await http.post("/push/unsubscribe", {
      endpoint: subscription.endpoint,
    });
  } catch {
    // ignore - user may already be logged out
  }

  await subscription.unsubscribe();
}
